import * as Yup from 'yup';
import { PAYMENT_METHOD, RECIPIENT_TYPES } from 'components/common/constants';
import {
  LATIN_LETTERS_AND_DIGITS_REG_EXP,
  START_WITH_GB_REG_EXP,
  GBP_SORT_CODE_REG_EXP,
  getCustomerNameRegExp,
  validateCommentFieldByPaymentMethod,
  getAccountNumberRegExp
} from './utils';
import { getMaxDescriptionSize } from '../../utils';
import { formatSortCode, trimTextContent } from 'services/utils';

export default ({
  i18n,
  onChange,
  onSubmit,
  customerNumber,
  model,
  currency,
  previousTransaction,
  providerData,
  paymentMethod
}) => ({
  enableReinitialize: true,
  validateOnChange: false,
  validateOnBlur: false,
  initialValues: {
    amount: `${previousTransaction?.amount || providerData?.amount || ''}`,
    recipient_wallet: previousTransaction?.counterparty?.iban || providerData?.recipient_wallet || '',
    recipient_type:
      RECIPIENT_TYPES[previousTransaction?.recipient_type] ||
      RECIPIENT_TYPES[previousTransaction?.counterparty?.party_type] ||
      providerData?.recipient_type ||
      RECIPIENT_TYPES.COMPANY,
    company_name: previousTransaction?.counterparty?.party_name || providerData?.company_name,
    first_name:
      (previousTransaction?.counterparty?.first_name ? previousTransaction?.counterparty?.first_name : null) ||
      (previousTransaction?.recipient_first_name ? previousTransaction?.recipient_first_name : null) ||
      previousTransaction?.recipient_name?.split(' ')[0] ||
      providerData?.first_name,
    last_name:
      (previousTransaction?.counterparty?.last_name ? previousTransaction?.counterparty?.last_name : null) ||
      (previousTransaction?.recipient_last_name ? previousTransaction?.recipient_last_name : null) ||
      previousTransaction?.recipient_name?.split(' ').slice(1).join(' ') ||
      providerData?.last_name,
    comment: previousTransaction?.comment || providerData?.comment,
    documents: null,
    account_number: previousTransaction?.counterparty?.account_number || providerData?.account_number || '',
    sort_code: formatSortCode(previousTransaction?.counterparty?.sort_code) || providerData?.sort_code || ''
  },
  initialStatus: { company_name: true, recipient_wallet: true },
  validationSchema: Yup.object().shape(
    {
      recipient_wallet: Yup.string().when('account_number', {
        is: (account_number) => !account_number,
        then: (schema) =>
          schema
            .nullable(true)
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(34, i18n.getMessage('client.validation.message.maxSize', { size: 34 }))
            .trim()
            // trick for using `currency` variable in checking value in validation
            .when('recipient_type', (recipient_type, schema) => {
              if (currency === 'GBP' && paymentMethod !== PAYMENT_METHOD.SWIFT) {
                return schema.matches(START_WITH_GB_REG_EXP, {
                  message: i18n.getMessage('client.validation.message.startWithGB')
                });
              }
              return schema;
            })
            .matches(LATIN_LETTERS_AND_DIGITS_REG_EXP, {
              message: i18n.getMessage('client.validation.message.isOnlyLatinLettersAndDigits')
            }),
        otherwise: (schema) => schema.nullable(true)
      }),
      account_number: Yup.string().when('recipient_wallet', {
        is: (recipient_wallet) => !recipient_wallet,
        then: (schema) =>
          schema
            .nullable(true)
            .required(i18n.getMessage('client.validation.message.isEmpty'))
            .max(50, i18n.getMessage('client.validation.message.maxSize', { size: 50 }))
            .min(7, i18n.getMessage('client.validation.message.minSize', { size: 7 }))
            .matches(getAccountNumberRegExp(), {
              message: i18n.getMessage('transfer.form.error.accountNumber')
            }),
        otherwise: (schema) => schema.nullable(true)
      }),
      sort_code: Yup.string().when('recipient_type', (recipient_type, schema) => {
        if (currency === 'GBP') {
          return schema.nullable(true).matches(GBP_SORT_CODE_REG_EXP, {
            message: i18n.getMessage('client.validation.message.sortCodeFormat')
          });
        }
        return schema.nullable(true);
      }),
      amount: Yup.string().nullable(true).required(i18n.getMessage('client.validation.message.isEmpty')),
      recipient_type: Yup.string().nullable(false).required(i18n.getMessage('client.validation.message.isEmpty')),
      company_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.COMPANY,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(70, i18n.getMessage('client.validation.message.maxSize', { size: 70 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      first_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.INDIVIDUAL,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      last_name: Yup.string()
        .nullable(true)
        .when('recipient_type', {
          is: RECIPIENT_TYPES.INDIVIDUAL,
          then: (schema) =>
            schema
              .required(i18n.getMessage('client.validation.message.isEmpty'))
              .max(35, i18n.getMessage('client.validation.message.maxSize', { size: 35 }))
              .matches(getCustomerNameRegExp(), {
                message: i18n.getMessage('client.validation.message.isOnlyLatinLettersDigitsSymbols', {
                  availableSymbols: "“.“, “,“, “-“, “/“, “:“, “?“, “(“, “)“, “'“, “+“, “CrLf“, “Space“"
                })
              }),
          otherwise: (schema) => schema.nullable(true)
        }),
      comment: Yup.string()
        .nullable(true)
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .test(
          'clear-junction-comment-validation',
          paymentMethod === PAYMENT_METHOD.SWIFT
            ? i18n.getMessage('transfer.form.error.comment.SWIFT')
            : paymentMethod === PAYMENT_METHOD.FASTER_PAYMENTS
              ? i18n.getMessage('transfer.form.error.comment.FASTER_PAYMENTS')
              : i18n.getMessage('transfer.form.error.comment.OTHER_METHODS'),
          (comment) => validateCommentFieldByPaymentMethod(paymentMethod, trimTextContent(comment))
        )
        .max(
          getMaxDescriptionSize(paymentMethod),
          i18n.getMessage('client.validation.message.maxSize', {
            size: getMaxDescriptionSize(paymentMethod)
          })
        )
    },
    [['account_number', 'recipient_wallet']]
  ),
  onSubmit: (values) => {
    onChange(values);
    onSubmit({ customerNumber, providerData: values, model });
  }
});
